/* Guided vertical coop door; upper fixed actuator body, lower rod pin on door.
   All lengths are inches and forces lbf. Pose 0 is closed and 1 is raised.
   The actuator retracts to open. Forces are quasi-static, without a hidden
   efficiency or safety multiplier. Guide resistance excludes door weight.
   This planar model does not certify bracket strength, guide clearance,
   obstruction sensing, holding/braking capacity or animal-safe closing. */
(function (root) {
  'use strict';
  const VERSION = '3.0.0';
  const EPS = 1e-8;
  const DEFAULTS = Object.freeze({
    openingWidth: 14, openingHeight: 14, doorWidth: 16, doorHeight: 16,
    doorWeight: 8, guideResistance: 2, travel: 16, doorPinOffset: 0,
    upperPinHeight: 52
  });
  const finite = value => typeof value === 'number' && Number.isFinite(value);
  const clamp = (value, low, high) => Math.min(high, Math.max(low, value));
  // `travel` is a compatibility alias, never an independent design input.
  // A full opening command lifts the sheet by its own complete height.
  const normalize = input => {
    const state = {...DEFAULTS, ...input};
    state.travel = state.doorHeight;
    return state;
  };
  const labels = {
    openingWidth: 'opening width', openingHeight: 'opening height',
    doorWidth: 'door width', doorHeight: 'door height', doorWeight: 'door weight',
    guideResistance: 'guide resistance', travel: 'opening travel',
    doorPinOffset: 'door pin offset', upperPinHeight: 'upper fixed pin height'
  };
  function resizeGeometry(previous = DEFAULTS, key, value, openPinClearance) {
    if (!previous || typeof previous !== 'object' || Array.isArray(previous)) throw new TypeError('A previous door state is required.');
    if (!Object.prototype.hasOwnProperty.call(DEFAULTS, key)) throw new RangeError('Unknown door input: ' + key);
    const before = normalize(previous), state = {...before, [key]: value};
    const changed = new Set();
    const positive = n => finite(n) && n > 0;
    const assign = (field, next) => {
      if (finite(next) && !Object.is(state[field], next)) {state[field] = next; changed.add(field);}
    };
    const finish = () => ({state: {...state, travel: state.doorHeight}, changes: [...changed].filter(field => field !== key && field !== 'travel')
      .map(field => ({key: field, from: before[field], to: state[field]}))});
    if (key === 'travel') return finish();
    // Preserve incomplete or invalid edits for the normal validator. Manual
    // bracket/pin/load edits never trigger an automatic size or mount change.
    if (!positive(value) || !['openingWidth', 'openingHeight', 'doorWidth', 'doorHeight'].includes(key)) return finish();
    const overlap = (door, opening) => finite(before[door]) && finite(before[opening])
      ? Math.max(0, before[door] - before[opening]) : 0;
    if (key === 'openingWidth' && positive(before.doorWidth)) {
      assign('doorWidth', Math.max(before.doorWidth, value + 2));
    }
    if (key === 'openingHeight') {
      if (positive(before.doorHeight)) assign('doorHeight', Math.max(before.doorHeight, value + 2));
    }
    // Use a fixed 2-inch minimum overlap, not the temporary excess left by a
    // smaller opening. Repeated slider up/down cycles must not ratchet the door.
    // The typed door dimension is authoritative. If it no longer covers the
    // opening, reduce that opening rather than silently replacing the edit.
    // Limit the retained overlap to 2 inches while keeping the opening at the
    // UI's 4-inch minimum when the edited door is at least that large. A 4-inch
    // door therefore has zero overlap; a sub-minimum edit remains visible for
    // the UI range validator rather than being silently replaced.
    for (const [door, opening] of [['doorWidth', 'openingWidth'], ['doorHeight', 'openingHeight']]) {
      if (key === door && positive(before[opening]) && value < before[opening]) {
        const margin = Math.min(overlap(door, opening), 2, Math.max(0, value - 4));
        assign(opening, value - margin);
      }
    }
    if (key === 'doorHeight' && finite(before.doorPinOffset) && before.doorPinOffset > value) assign('doorPinOffset', value);
    if (['openingHeight', 'doorHeight'].includes(key)) {
      const oldRaisedPin = 2 * before.doorHeight - before.doorPinOffset;
      const newRaisedPin = 2 * state.doorHeight - state.doorPinOffset;
      // The UI retains a stable intended open-pin clearance across automatic
      // resizes, updating it only after a manual mounting/pin edit or Fit.
      // Otherwise repeated down/up slider cycles would turn the temporarily
      // enlarged gap into a request to raise the frame yet again. The optional
      // argument defaults to the previous gap for simple one-shot callers.
      // Negative gaps are not repaired: an invalid manual mount stays invalid.
      const clearance = openPinClearance === undefined ? before.upperPinHeight - oldRaisedPin : openPinClearance;
      if (finite(newRaisedPin) && finite(before.upperPinHeight) && finite(clearance)) {
        assign('upperPinHeight', Math.max(before.upperPinHeight, newRaisedPin + clearance));
      }
    }
    return finish();
  }
  function inspect(input, ignoreMount = false) {
    const errors = [], warnings = [];
    if (input == null || typeof input !== 'object' || Array.isArray(input)) {
      return {state: {...DEFAULTS}, errors: ['Enter the door dimensions and mounting height.'], warnings};
    }
    const state = normalize(input);
    for (const key of Object.keys(DEFAULTS)) {
      if (!finite(state[key])) errors.push('Enter a finite number for ' + labels[key] + '.');
    }
    if (errors.length) return {state, errors, warnings};
    for (const key of ['openingWidth', 'openingHeight', 'doorWidth', 'doorHeight', 'travel']) {
      if (state[key] <= 0) errors.push('The ' + labels[key] + ' must be greater than zero.');
    }
    if (state.doorWeight < 0 || state.guideResistance < 0) errors.push('Door weight and guide resistance cannot be negative.');
    if (state.doorWidth + EPS < state.openingWidth) errors.push('The door must be at least as wide as the opening to cover it when closed.');
    if (state.doorHeight + EPS < state.openingHeight) errors.push('The door must be at least as tall as the opening to cover it when closed.');
    if (state.doorPinOffset < 0 || state.doorPinOffset > state.doorHeight) errors.push('Place the moving pin between the top and bottom of the door.');
    const topPinY = state.doorHeight - state.doorPinOffset + state.travel;
    if (![topPinY, state.doorHeight + state.travel, state.doorWeight + state.guideResistance,
      state.upperPinHeight - state.doorHeight + state.doorPinOffset].every(Number.isFinite)) {
      errors.push('The entered dimensions or loads are too large to calculate.');
    }
    if (!ignoreMount && state.automaticMount === true) {
      const hardware = inspectActuator(state.automaticActuator);
      errors.push(...hardware.errors);
      const closedLength = state.upperPinHeight - state.doorHeight + state.doorPinOffset;
      if (closedLength <= EPS) errors.push('The upper fixed pin must be above the closed door pin.');
      if (hardware.range) {
        if (closedLength < hardware.range.min - EPS || closedLength > hardware.range.max + EPS) {
          errors.push('The automatic mounting position no longer connects this actuator to the closed door. Refit the selected actuator.');
        } else if (state.travel > closedLength - hardware.range.min + EPS) {
          warnings.push('The requested opening travel exceeds this actuator’s available retraction. The connected door stops at the hardware limit.');
        }
      }
    } else if (!ignoreMount && state.upperPinHeight <= topPinY + EPS) errors.push('The upper fixed pin must stay above the moving pin throughout the opening travel.');
    if (state.travel + EPS < state.openingHeight) warnings.push('This travel leaves part of the doorway covered at the fully raised position.');
    if (Math.abs(state.doorWidth - state.openingWidth) <= EPS) warnings.push('There is no door overlap at the sides of the opening. Allow enough overlap for the guide arrangement.');
    if (Math.abs(state.doorHeight - state.openingHeight) <= EPS) warnings.push('There is no door overlap above the opening when closed. Check the top seal and guide arrangement.');
    return {state, errors, warnings};
  }
  function requirements(p) {
    const closedPinLength = p.upperPinHeight - p.doorHeight + p.doorPinOffset;
    const openPinLength = closedPinLength - p.travel;
    const liftingForce = p.doorWeight + p.guideResistance;
    const closingPushForce = Math.max(0, p.guideResistance - p.doorWeight);
    const closingRestraintForce = Math.max(0, p.doorWeight - p.guideResistance);
    return {closedPinLength, openPinLength, requestedClosedPinLength: closedPinLength,
      requestedOpenPinLength: openPinLength, requiredStroke: p.doorHeight, requiredTravel: p.doorHeight,
      liftingForce, closingPushForce, closingRestraintForce,
      signedOpeningForce: -liftingForce, signedClosingForce: p.guideResistance - p.doorWeight,
      peakForce: Math.max(liftingForce, closingPushForce, closingRestraintForce), openingRetracts: true};
  }
  function evaluate(input = DEFAULTS, requestedPose = 0) {
    const checked = inspect(input), {state: p, errors, warnings} = checked;
    if (!finite(requestedPose) || requestedPose < 0 || requestedPose > 1) errors.push('Animation position must be between 0 and 1.');
    if (errors.length) return {version: VERSION, valid: false, errors, warnings, state: p, pose: requestedPose, requestedPose, geometry: null};
    const required = requirements(p), automatic = p.automaticMount === true;
    const hardware = automatic ? inspectActuator(p.automaticActuator) : null;
    const availableTravel = automatic ? Math.max(0, required.closedPinLength - hardware.range.min) : p.travel;
    const actualTravel = Math.min(p.travel, availableTravel);
    // Playback spans the entire achievable lift uniformly. A short actuator
    // must keep moving throughout 0..1 rather than reaching its stop early.
    const actualMovement = requestedPose * actualTravel;
    const pose = actualMovement / p.doorHeight;
    const doorBottomY = actualMovement, doorTopY = doorBottomY + p.doorHeight;
    // Both pins and the load act on the centered vertical axis. Side rails
    // are stationary and shown long enough to guide the complete motion.
    const fixedPin = [0, p.upperPinHeight];
    const movingPin = [0, doorTopY - p.doorPinOffset];
    const pinLength = fixedPin[1] - movingPin[1];
    const closedPinLength = required.closedPinLength;
    const openPinLength = closedPinLength - actualTravel;
    const clearOpeningHeight = Math.min(p.openingHeight, doorBottomY);
    const blockedOpeningHeight = p.openingHeight - clearOpeningHeight;
    const requestedDoorBottomY = p.travel * requestedPose;
    const requestedMovingPin = [0, p.doorHeight - p.doorPinOffset + requestedDoorBottomY];
    const requestedPinLength = p.upperPinHeight - requestedMovingPin[1];
    if (automatic && actualTravel + EPS < p.openingHeight && p.travel + EPS >= p.openingHeight) {
      warnings.push('The selected actuator cannot lift the door far enough to clear the full doorway.');
    }
    const geometry = {
      fixedPin, movingPin, pinLength, doorBottomY, doorTopY,
      doorLeftX: -p.doorWidth / 2, doorRightX: p.doorWidth / 2,
      openingLeftX: -p.openingWidth / 2, openingRightX: p.openingWidth / 2,
      railLeftX: -p.doorWidth / 2, railRightX: p.doorWidth / 2,
      railBottomY: 0, railTopY: p.doorHeight + p.travel,
      sideOverlap: (p.doorWidth - p.openingWidth) / 2,
      closedTopOverlap: p.doorHeight - p.openingHeight,
      clearOpeningHeight, blockedOpeningHeight
    };
    return {
      ...required,
      version: VERSION, valid: true, errors, warnings, state: p, pose, actualPose: pose, requestedPose, progress: requestedPose, geometry,
      fixedPin, movingPin, pinLength, doorBottomY, doorTopY,
      automaticMount: automatic, availableTravel, actualTravel, actualMovement: doorBottomY,
      limited: actualTravel + EPS < p.doorHeight, travelLimited: actualTravel + EPS < p.doorHeight,
      requestedDoorBottomY, requestedDoorTopY: requestedDoorBottomY + p.doorHeight,
      requestedMovingPin, requestedPinLength,
      clearOpeningHeight, blockedOpeningHeight,
      fullyOpen: blockedOpeningHeight <= EPS,
      fullOpeningPossible: actualTravel + EPS >= p.openingHeight,
      requestedFullOpeningPossible: p.travel + EPS >= p.openingHeight,
      closedPinLength, openPinLength, requiredStroke: p.doorHeight, requiredTravel: p.doorHeight,
      requestedOpenPinLength: required.openPinLength
    };
  }
  function inspectActuator(actuator) {
    const errors = [];
    if (!actuator || typeof actuator !== 'object') return {errors: ['Select an actuator with known pin lengths and stroke.'], range: null, forceKnown: false};
    for (const key of ['closedIn', 'openIn', 'strokeIn']) {
      if (!finite(actuator[key]) || actuator[key] <= 0) errors.push('The selected actuator needs a positive ' + key + ' value.');
    }
    if (!errors.length && actuator.openIn <= actuator.closedIn) errors.push('The actuator extended pin length must exceed its retracted pin length.');
    if (actuator.forceLbf != null && (!finite(actuator.forceLbf) || actuator.forceLbf < 0)) errors.push('The selected actuator force rating must be a finite nonnegative value.');
    if (errors.length) return {errors, range: null, forceKnown: false};
    // Published extended lengths may differ slightly from nominal stroke.
    // Use the smaller travel; never invent pin length by stretching the CAD.
    const usableStroke = Math.min(actuator.strokeIn, actuator.openIn - actuator.closedIn);
    return {errors, forceKnown: finite(actuator.forceLbf), range: {
      min: actuator.closedIn, max: actuator.closedIn + usableStroke, usableStroke,
      publishedOpen: actuator.openIn, nominalStroke: actuator.strokeIn
    }};
  }
  function actuatorFit(input = DEFAULTS, actuator) {
    // Compare requested endpoints with the supplied candidate, never the old
    // automatic hardware snapshot or its clamped animation. This result is a
    // requirements record, not an actual physical pose.
    const checked = inspect(input, true), hardware = inspectActuator(actuator);
    // In automatic mode each candidate gets its own fixed upper mounting
    // proposal. Comparing it at another model's bracket position would reject
    // usable hardware. Manual mode continues to use the entered fixed bracket.
    if (!checked.errors.length && !hardware.errors.length && checked.state.automaticMount === true) {
      const position = automaticPosition(checked.state, hardware.range);
      if (!finite(position.upperPinHeight)) checked.errors.push('The mounting dimensions are too large to calculate.');
      else checked.state = {...checked.state, upperPinHeight: position.upperPinHeight,
        automaticActuator: {...actuator}};
    }
    const result = {version: VERSION, valid: checked.errors.length === 0, ...checked,
      requestedOnly: true, geometry: null, ...(checked.errors.length ? {} : requirements(checked.state))};
    const errors = [...result.errors, ...hardware.errors], reasons = errors.slice();
    const empty = {exists: false, minPose: null, maxPose: null, fullTravel: false};
    if (errors.length) return {valid: false, fits: false, geometryFits: false, forceFits: null,
      errors, reasons, warnings: result.warnings, state: result.state, range: hardware.range,
      reachable: empty, minPose: null, maxPose: null, result};
    const range = hardware.range;
    const lower = Math.max(0, (result.closedPinLength - range.max) / result.requiredStroke);
    const upper = Math.min(1, (result.closedPinLength - range.min) / result.requiredStroke);
    const exists = lower <= upper + EPS && upper >= -EPS && lower <= 1 + EPS;
    const geometryFits = result.openPinLength >= range.min - EPS && result.closedPinLength <= range.max + EPS;
    const reachable = {
      exists,
      minPose: exists ? clamp(lower, 0, 1) : null,
      maxPose: exists ? clamp(Math.max(lower, upper), 0, 1) : null,
      fullTravel: geometryFits
    };
    const forceFits = hardware.forceKnown ? actuator.forceLbf + EPS >= result.peakForce : null;
    if (result.requiredStroke > range.usableStroke + EPS) reasons.push('The selected actuator has less usable stroke than the requested door travel.');
    if (result.closedPinLength > range.max + EPS) reasons.push('The selected actuator cannot extend far enough to reach the door pin at the closed position.');
    if (result.openPinLength < range.min - EPS) reasons.push('The selected actuator cannot retract far enough for the requested raised position.');
    if (forceFits === false) reasons.push('The selected force rating is below the door weight plus measured guide resistance.');
    if (forceFits === null) reasons.push('The selected model has no known force rating to compare with the lifting load.');
    const warnings = [...result.warnings,
      'Compare the lifting load with the exact model’s pull rating and check its ability to hold the raised door.'];
    return {valid: true, fits: geometryFits && forceFits === true, geometryFits, forceFits,
      errors, reasons, warnings, state: result.state, range, reachable,
      minPose: reachable.minPose, maxPose: reachable.maxPose, result};
  }
  function automaticPosition(p, range) {
    const endMargin = Math.max(0, Math.min(.25, (range.usableStroke - p.doorHeight) / 2));
    return {endMargin, upperPinHeight: p.doorHeight - p.doorPinOffset + range.max - endMargin};
  }
  function autoMount(input = DEFAULTS, actuator) {
    const checked = inspect(input, true), hardware = inspectActuator(actuator);
    const errors = [...checked.errors, ...hardware.errors];
    if (errors.length) return {valid: false, state: {...checked.state}, fit: null, errors, message: errors.join(' ')};
    const p = checked.state, range = hardware.range;
    const {endMargin, upperPinHeight} = automaticPosition(p, range);
    if (!finite(upperPinHeight)) return {valid: false, state: {...p}, fit: null, errors: ['The mounting dimensions are too large to calculate.'], message: 'The mounting dimensions are too large to calculate.'};
    const snapshot = {closedIn: actuator.closedIn, openIn: actuator.openIn, strokeIn: actuator.strokeIn, forceLbf: actuator.forceLbf};
    const state = {...p, upperPinHeight, automaticMount: true, automaticActuator: snapshot};
    const result = evaluate(state), fit = actuatorFit(state, actuator);
    return {valid: result.valid, state, fit, result, errors: result.errors, endMargin,
      actualTravel: result.actualTravel, requestedTravel: p.travel,
      moved: Math.abs(upperPinHeight - p.upperPinHeight) > EPS,
      message: 'The upper bracket is positioned to keep the selected actuator connected to the closed door. It stays fixed throughout playback.' +
        (result.actualTravel + EPS < p.doorHeight ? ' The door height still determines the required stroke; this actuator provides less lift.' : '')};
  }
  function fitMount(input = DEFAULTS, actuator) {
    // Explicit proposal only: this function never mutates the caller's state.
    // It can repair a low overhead mount but cannot improve force or stroke.
    const checked = inspect(input, true), hardware = inspectActuator(actuator);
    const errors = [...checked.errors, ...hardware.errors];
    if (errors.length) return {valid: false, state: {...checked.state}, fit: null, errors, message: errors.join(' ')};
    const p = checked.state, range = hardware.range;
    if (p.travel > range.usableStroke + EPS) {
      const message = 'Moving the upper bracket cannot make up for insufficient actuator stroke. Choose a longer stroke or reduce the door height.';
      return {valid: false, state: {...p}, fit: actuatorFit(p, actuator), errors: [message], message};
    }
    const endMargin = Math.max(0, Math.min(.25, (range.usableStroke - p.travel) / 2));
    const upperPinHeight = p.doorHeight - p.doorPinOffset + range.max - endMargin;
    const state = {...p, upperPinHeight};
    if (state.automaticMount === true) state.automaticActuator = {closedIn: actuator.closedIn, openIn: actuator.openIn, strokeIn: actuator.strokeIn, forceLbf: actuator.forceLbf};
    const fit = actuatorFit(state, actuator);
    return {valid: fit.valid && fit.geometryFits, state, fit, errors: [], endMargin,
      moved: Math.abs(upperPinHeight - p.upperPinHeight) > EPS,
      message: 'The proposed upper fixed pin height fits the requested travel. Confirm space for the actuator body and a rigid bracket at this height.' +
        (fit.forceFits === false ? ' The selected actuator force rating is still too low.' : fit.forceFits === null ? ' The selected force rating still needs checking.' : '')};
  }
  const api = {version: VERSION, DEFAULTS, defaults: DEFAULTS, resizeGeometry, evaluate, actuatorFit, fitMount, autoMount};
  root.FGChickenCoopModel = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
